import propTypes from 'prop-types'
import React, { useCallback, useMemo, useEffect } from 'react'
import { useRouter } from 'next/router'
import styles from './index.module.scss'
import { useAnalytics } from '../../../common/analytics'
import { footerData } from './footerData'
import SocialMediaIcon from '../../shared/SocialMediaIcon'
import {
  defaultFooterText,
  spanishFooterText,
  visualizationBarChartFooterText,
  homeVariationFooterText,
  footerTextVariation2,
  footerTextVariation5,
  footerTextVariation3,
  footerCFOSText,
  inactiveLeadWelcomePopupDisclosures,
  footerTextVariation18,
} from '../consts'
// footerCFOSText is intentionally kept — it is the permanent footer text after fdr-cfos-disclosures test ended (control won)
import UnCrawlableLink from '../../shared/UnCrawlableLink'
import { isInternalForTealium } from '../../../common/helpers'
import DynamicTrustMarkers from '../../shared/DynamicTrustMarkers'
import Hyperlink from '../../shared/HyperLink'
import { APP_BASE_PATH } from 'src/common/constants'
import classNames from 'classnames'
import { allowedRouteList } from 'src/common/allowed-route-list'
import ClientSideContent from 'src/components/shared/ClientSideContent'
import getConfig from 'src/common/runtimeConfig'
import {
  loadDisclosuresUrl as defaultLoadDisclosuresUrl,
  processDisclosureUrl as defaultProcessDisclosureUrl,
} from 'src/common/constants/constants'
import Script from 'next/script'
import { LogosGroup } from 'src/components/shared/Awards/consts'
import { Section } from 'src/components/shared/SectionV2'
import { useClientDashboardLoginOverlayTrigger } from '../useClientDashboardLoginOverlayTrigger'

const dashboardButtonFont = '@apply text-base font-normal'
const { LEGAL_BASE_PATH } = getConfig()?.publicRuntimeConfig

const estimatesDisclosureRoutes = [
  '/',
  '/how-it-works/',
  '/who-we-help/',
  '/why-were-better/',
  '/testimonials/',
  '/about/',
  '/scam/',
]

const translateData = (homeVariation) => {
  const getFooterText = () => {
    switch (homeVariation) {
      case 'control':
        return defaultFooterText
      case 'variant-4':
        return visualizationBarChartFooterText
      case 'variant-2':
        return footerTextVariation2
      case 'variant-3':
        return footerTextVariation3
      case 'variant-5':
        return footerTextVariation5
      case 'variant-6':
        return homeVariationFooterText
      case 'variant-18':
        return footerTextVariation18
      case 'control-ny':
      default:
        return footerCFOSText
    }
  }

  return {
    en: {
      cal_privacy_notice_path: `${LEGAL_BASE_PATH}/privacy/freedom-debt-relief-california-privacy-disclosures#your-california-privacy-rights`,
      my_personal_information_path: '/do-not-sell-my-personal-information/',
      privacy_path: `${LEGAL_BASE_PATH}/privacy/privacy-policy/`,
      terms_path: `${LEGAL_BASE_PATH}/privacy/terms/`,
      privacy_policy: `${LEGAL_BASE_PATH}/privacy/freedom-debt-relief-california-privacy-disclosures/`,
      footerText: getFooterText(),
    },
    es: {
      cal_privacy_notice_path: `${LEGAL_BASE_PATH}/privacy/freedom-debt-relief-california-privacy-disclosures-es#actualizaciones-a-estas-divulgaciones-de-ca`,
      my_personal_information_path: '/do-not-sell-my-personal-information-es/',
      privacy_path: `${LEGAL_BASE_PATH}/privacy/politica-de-privacidad/`,
      terms_path: `${LEGAL_BASE_PATH}/privacy/terminos-de-servicio/`,
      privacy_policy: `${LEGAL_BASE_PATH}/privacy/freedom-debt-relief-california-privacy-disclosures-es/`,
      footerText: spanishFooterText,
    },
  }
}

const renderFooterText = (footerText) => {
  if (typeof footerText === 'string') {
    return footerText
  }

  if (Array.isArray(footerText)) {
    return footerText.map((item, index) => {
      if (item.noCrawl) {
        return (
          <ClientSideContent key={index}>
            <span className="block">{item.text}</span>
          </ClientSideContent>
        )
      }

      return (
        <span key={index} className="block">
          {item.text}
        </span>
      )
    })
  }

  return ''
}

const Footer = ({
  footerClassName,
  locale,
  crawlable,
  isHome,
  statusCode,
  homeVariation,
  shouldShowInactiveLeadWelcomePopup,
  loadDisclosuresUrl = defaultLoadDisclosuresUrl,
  processDisclosureUrl = defaultProcessDisclosureUrl,
  hideClientDashboardLink = false,
}) => {
  const router = useRouter()
  const pathname = router.asPath.split('?')[0].split('#')[0]
  const { overlayEnabled, openOverlayInsteadOfNavigating } =
    useClientDashboardLoginOverlayTrigger()

  const footerParagraph = useMemo(
    () => translateData(homeVariation)[locale],
    [locale, homeVariation]
  )

  const { track } = useAnalytics()

  useEffect(() => {
    window.disclosureUrl = loadDisclosuresUrl
    setTimeout(() => {
      if (
        typeof window !== 'undefined' &&
        window?.disclosureData &&
        window.processDisclosures
      ) {
        window.processDisclosures()
      }
    }, 1000)
  }, [loadDisclosuresUrl])

  const handleFooterLinkClick = useCallback(
    (href, name) => {
      const clickId = name.replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
      const data = {
        nav_link_section: 'Footer',
        click_type: 'Link Click',
        click_id: clickId,
        click_url: href,
        click_text: `FDR-Web | ${name}`,
        event_type: 'track',
        track_event: 'global_footer',
        outbound_url: !isInternalForTealium(href) ? href : '',
      }

      track(data, {}, 'click', statusCode)

      if (allowedRouteList.includes(href)) {
        window.location.href = window.location.origin + href
      }
    },
    [track, statusCode]
  )

  const handleButtonClick = useCallback(
    (href, name) => {
      const data = {
        nav_link_section: 'Footer',
        click_type: 'Button Click',
        click_id: name,
        click_url: href,
        click_text: `FDR-Web | ${name}`,
        track_event: 'global_footer',
      }

      track(data, {}, 'click', statusCode)
    },
    [track, statusCode]
  )
  const handleSocialIconClick = useCallback(
    (href, name) => {
      const data = {
        nav_link_section: 'Footer',
        click_type: 'Social Media Click',
        click_id: name,
        click_url: href,
        click_text: `FDR-Web | ${name}`,
        track_event: 'global_footer',
      }
      track(data, {}, 'click', statusCode)
    },
    [track, statusCode]
  )

  const handleNonCrawlableLinkClick = useCallback(
    async (item) => {
      handleFooterLinkClick(item.url, `${item.name} - ${item.name}`)
      await router.push(item.url)
    },
    [router, handleFooterLinkClick]
  )

  return (
    <footer
      className={classNames(
        `mx-auto bg-secondary px-4 py-12 text-center md:text-left ${footerClassName}`
      )}
      data-testid="footer"
    >
      {typeof window !== 'undefined' && window.disclosureUrl && (
        <Script src={processDisclosureUrl} strategy="afterInteractive" />
      )}
      <div className="lg:max-w-272 mx-auto">
        <div className="flex flex-col-reverse lg:flex-row lg:justify-between">
          {footerData.map(({ id, subItems }) => (
            <section
              className={`mb-8 lg:mb-0 ${
                id === 'company' ? 'mb-4 w-full md:mr-0' : 'mb-4 w-full md:mr-0'
              }`}
              aria-label="footer-section"
              key={id}
            >
              <ul
                className="font-normal text-gray-900"
                data-testid="footer-section"
              >
                {Object.keys(subItems).map((item) => {
                  if (locale === 'es' && item === 'espanol') {
                    subItems[item].url = '/'
                    subItems[item].name = 'English'
                  } else if (locale === 'en' && item === 'espanol') {
                    subItems[item].url = '/es/espanol/'
                    subItems[item].name = 'Español'
                  }

                  return (
                    <li
                      className="mb-2 text-center lg:mb-3 lg:text-left"
                      key={item}
                      data-testid="footer-section-item"
                    >
                      {subItems[item].name === 'Unsubscribe' && !crawlable ? (
                        <span
                          className="cursor-pointer text-sm"
                          onClick={() =>
                            handleNonCrawlableLinkClick(subItems[item])
                          }
                        >
                          {subItems[item].name}
                        </span>
                      ) : item === 'login' ? (
                        <a
                          href={subItems[item].url}
                          rel={overlayEnabled ? undefined : 'noopener'}
                          target={overlayEnabled ? undefined : '_blank'}
                          className="text-sm"
                          onClick={(e) => {
                            openOverlayInsteadOfNavigating(e)
                            handleFooterLinkClick(
                              e.currentTarget.href,
                              subItems[item].name
                            )
                          }}
                        >
                          {subItems[item].name}
                        </a>
                      ) : (
                        <Hyperlink
                          href={subItems[item].url}
                          prefetch={false}
                          childrenClassNames="text-sm"
                          onClick={(e) => {
                            if (allowedRouteList.includes(subItems[item].url)) {
                              e.preventDefault()
                            }

                            handleFooterLinkClick(
                              subItems[item].url,
                              subItems[item].name
                            )
                          }}
                        >
                          {subItems[item].name}
                        </Hyperlink>
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}

          <section
            role="complementary"
            className={classNames(
              'md:order-0 order-first w-full md:w-[20%] md:text-right lg:w-2/12',
              hideClientDashboardLink && 'self-start'
            )}
          >
            <div className="mx-auto mb-14 flex max-w-[207px] flex-col gap-6">
              {hideClientDashboardLink ? null : (
                <section data-testid="footer-client-dashboard-link">
                  <a
                    href={`${APP_BASE_PATH()}/login`}
                    rel={overlayEnabled ? undefined : 'noopener'}
                    target={overlayEnabled ? undefined : '_blank'}
                    className="rounded-lg border-2 border-content-accent px-6 py-3 text-content-accent"
                    aria-label="Client Dashboard link"
                    onClick={(e) => {
                      openOverlayInsteadOfNavigating(e)
                      handleButtonClick(
                        e.currentTarget.href,
                        'Client Dashboard'
                      )
                    }}
                  >
                    <Section.Typography
                      as="span"
                      variant="label-button-medium"
                      className="whitespace-nowrap align-middle text-content-accent"
                    >
                      Client Dashboard
                    </Section.Typography>
                  </a>
                </section>
              )}

              <div
                className={classNames(
                  'flex items-center justify-center',
                  hideClientDashboardLink ? 'mt-0 mb-10' : 'my-10'
                )}
                data-testid="footer-social-icons"
              >
                <SocialMediaIcon
                  id="footer-facebook"
                  type="facebook"
                  url="https://www.facebook.com/pages/Freedom-Debt-Relief/69869352985?ref=hl"
                  onClick={() =>
                    handleSocialIconClick(
                      'https://www.facebook.com/pages/Freedom-Debt-Relief/69869352985?ref=hl',
                      'Facebook'
                    )
                  }
                />
                <SocialMediaIcon
                  id="footer-instagram"
                  type="instagram"
                  url="https://instagram.com/freedomdebtrelief"
                  onClick={() =>
                    handleSocialIconClick(
                      'https://instagram.com/freedomdebtrelief',
                      'Instagram'
                    )
                  }
                />
                <SocialMediaIcon
                  id="footer-youtube"
                  type="youtube"
                  url="https://www.youtube.com/freedomdebtrelief"
                  onClick={() =>
                    handleSocialIconClick(
                      'https://www.youtube.com/freedomdebtrelief',
                      'Youtube'
                    )
                  }
                />
                <SocialMediaIcon
                  id="footer-twitter"
                  type="twitter"
                  url="https://twitter.com/FreedomDebt"
                  onClick={() =>
                    handleSocialIconClick(
                      'https://twitter.com/FreedomDebt',
                      'Twitter'
                    )
                  }
                />
                <SocialMediaIcon
                  id="footer-linkedIn"
                  type="linkedin"
                  url="https://www.linkedin.com/company/freedom-debt-relief"
                  onClick={() =>
                    handleSocialIconClick(
                      'https://www.linkedin.com/company/freedom-debt-relieft',
                      'LinkedIn'
                    )
                  }
                />
              </div>
            </div>
          </section>
        </div>
        {isHome ? null : (
          <DynamicTrustMarkers
            combinationId="home-variant-8"
            isDynamic={true}
            logosGroup={LogosGroup.mobileGroup.groupTwo}
            useGridLayout={true}
          />
        )}
        <div className="w-content flex flex-col py-4 text-left md:flex-row">
          <div
            className="mr-0 mb-4 text-12"
            data-testid="footer-disclaimer-links"
          >
            <div
              className={`${styles.disclaimerLink} my-2`}
              data-testid="footer-disclaimer-links-container"
            >
              {crawlable ? (
                <>
                  <a
                    className="text-black-base visited:text-black-base hover:text-blue-700"
                    href={footerParagraph.privacy_policy}
                    data-testid="footer-privacy-policy"
                  >
                    Privacy policy
                  </a>
                  <a
                    href={footerParagraph.terms_path}
                    className="mr-1 text-black-base after:mx-[5px] after:content-['\1C0'] visited:text-black-base hover:text-blue-700"
                    data-testid="footer-terms"
                  >
                    Terms of Use
                  </a>
                  <a
                    href={footerParagraph.cal_privacy_notice_path}
                    className="mr-1 text-black-base after:mx-[5px] after:content-['\1C0'] visited:text-black-base hover:text-blue-700"
                    data-testid="footer-california-privacy"
                  >
                    California Privacy
                  </a>
                </>
              ) : (
                <>
                  {locale === 'es' ? (
                    <>
                      <UnCrawlableLink
                        className="text-black-base visited:text-black-base hover:text-blue-700"
                        label="Politica de privacidad"
                        href={footerParagraph.privacy_policy}
                        data-testid="footer-privacy-policy"
                      />
                      <UnCrawlableLink
                        className="text-black-base after:mx-[5px] after:content-['\1C0'] visited:text-black-base hover:text-blue-700"
                        label="Terms of Use"
                        href={footerParagraph.terms_path}
                        data-testid="footer-terms"
                      />
                      <UnCrawlableLink
                        className="text-black-base after:mx-[5px] after:content-['\1C0'] visited:text-black-base hover:text-blue-700"
                        label="California Privacy"
                        href={footerParagraph.cal_privacy_notice_path}
                        data-testid="footer-california-privacy"
                      />
                    </>
                  ) : (
                    <>
                      <UnCrawlableLink
                        className="text-black-base visited:text-black-base hover:text-blue-700"
                        label="Privacy policy"
                        href={footerParagraph.privacy_policy}
                        data-testid="footer-privacy-policy"
                      />
                      <UnCrawlableLink
                        className="text-black-base after:mx-[5px] after:content-['\1C0'] visited:text-black-base hover:text-blue-700"
                        label="Terms of Use"
                        href={footerParagraph.terms_path}
                        data-testid="footer-terms"
                      />
                      <UnCrawlableLink
                        className="text-black-base after:mx-[5px] after:content-['\1C0'] visited:text-black-base hover:text-blue-700"
                        label="California Privacy"
                        href={footerParagraph.cal_privacy_notice_path}
                        data-testid="footer-california-privacy"
                      />
                    </>
                  )}
                </>
              )}

              <UnCrawlableLink
                className="text-black-base visited:text-black-base hover:text-blue-700"
                label={
                  locale === 'es' ? 'Aviso de Privacidad' : 'Privacy Notice'
                }
                href={footerParagraph.privacy_path}
                data-testid="footer-notice"
              />
            </div>
            © {new Date().getFullYear()}. All rights reserved. Freedom Debt
            Relief, LLC is registered with DFPI under CCFPL - Registration
            numbers 01-CCFPL-1248929-3482303 and 02-CCFPL-1248929-3726479
          </div>
        </div>
        <div
          className="w-content mb-6 whitespace-pre-line text-left text-12 text-gray-500"
          data-testid="footer-diclosure-content"
        >
          {homeVariation === 'variant-18' && (
            <div
              className="mb-4"
              id="[(ACX-FDR-PAGE-DISCLOSURE-0-PARAGRAPH-0)]"
              data-testid="[(ACX-FDR-PAGE-DISCLOSURE-0-PARAGRAPH-0)]"
              no-crawl-id="[(ACX-FDR-PAGE-DISCLOSURE-0-PARAGRAPH-0)]"
            ></div>
          )}
          <div
            className="mb-4"
            id="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-1)]"
            data-testid="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-1)]"
            no-crawl-id="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-1)]"
          ></div>
          <div
            className="mb-4"
            id="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-2)]"
            data-testid="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-2)]"
            no-crawl-id="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-2)]"
          ></div>
          <div
            className="mb-4"
            id="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-2)]"
            data-testid="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-3)]"
            no-crawl-id="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-3)]"
          ></div>
          {estimatesDisclosureRoutes.includes(pathname) && (
            <>
              <p className="mb-4">
                ³ These estimates are not actual amounts or periods and are
                based on information you have provided, information from your
                credit report and/or information from third parties. Estimates
                are not for general use and are an approximation and not a
                guarantee. These estimates are based on assumptions derived from
                prior experience and publicly available information on interest
                rates, charges and minimum payment requirements for consumer
                debt, including the average APR on credit card accounts as
                reported in the Federal Reserve Board of Governor’s statistical
                release on Consumer Credit for the previous quarter and 29.99%
                for delinquent credit cards. These rates are blended with any
                other fixed term repayment and high interest loans you may have.
                Your actual rates, charges, fees and minimum payment
                requirements may be higher or lower. Actual estimates could also
                vary based on receipt of military benefits (SCRA and/or MLA).
              </p>
              <p className="mb-4">
                These estimates assume that there are no additional charges,
                rebates, refunds, payments or reductions of your debt other than
                the monthly payment of the estimated current and future total
                minimum monthly payment. This payment strategy is based on
                publicly available information about your enrolled accounts and
                applies the minimum monthly payments as a declining amount month
                over month as the principal balance is paid down.
              </p>
            </>
          )}

          {pathname === '/' && (
            <>
              <p className="mb-4">
                ⁴ This guarantee takes effect when you graduate the program—or
                even if you leave before graduating. When you exit the program,
                we’ll use the combined total of all the debts you enrolled and
                settled and the fees you paid to us to calculate your
                eligibility for a refund.
              </p>
            </>
          )}
          {pathname === '/why-were-better/' && (
            <span
              className="mb-2 block"
              id="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-8)]"
              data-testid="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-8)]"
              no-crawl-id="[(ACX-FDR-PAGE-DISCLOSURE-1-PARAGRAPH-8)]"
            ></span>
          )}
          {shouldShowInactiveLeadWelcomePopup && (
            <>
              <div className="mb-2">
                <span className="block">Popup disclosures:</span>
              </div>
              <div className="mb-2">
                <span className="block">
                  {inactiveLeadWelcomePopupDisclosures[0].text}
                </span>
              </div>
            </>
          )}

          <p className="mb-2">
            Freedom Debt Relief is located at 1875 S Grant St #300, San Mateo,
            CA 94402
          </p>
          {locale === 'es' ? (
            <p>
              Freedom Debt Relief, LLC (NMLS ID # 1248929) está registrada en
              DFPI bajo CCFPL - Números de registro 01-CCFPL-1248929-3482303 y
              02-CCFPL-1248929-3726479.
            </p>
          ) : (
            <p className="mb-2">
              Freedom Debt Relief, LLC (NMLS ID # 1248929) is registered with en
              under CCFPL - Registration numbers 01-CCFPL-1248929-3482303 and
              02-CCFPL-1248929-3726479.
            </p>
          )}
          <a
            href={'/about/freedom-debt-relief-and-achieve/'}
            className="hover:text-blue-700"
            data-testid="footer-achieve-relationship"
          >
            Understanding the relationship between Freedom Debt Relief and
            Achieve
          </a>
        </div>
      </div>
    </footer>
  )
}

Footer.defaultProps = {
  footerClassName: '',
  isHome: false,
  hideClientDashboardLink: false,
}

Footer.propTypes = {
  footerClassName: propTypes.string,
  locale: propTypes.string.isRequired,
  crawlable: propTypes.bool.isRequired,
  isHome: propTypes.bool,
  statusCode: propTypes.number,
  hideClientDashboardLink: propTypes.bool,
}

export default Footer
